import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

/**
 * Invalidate every cached flow-instance query after a portfolio-collection or
 * risk-model edit — call from `onSuccess` of any settings mutation whose API
 * counterpart runs `invalidateForProduct` / `invalidateDynamicForCollection` /
 * `invalidateForRiskLevel` / `invalidateForTimeHorizon`
 * (apps/api/src/services/snapshots/snapshot-invalidation-service.ts).
 *
 * The server already drops the persisted state — snapshot rows, generated
 * dynamic compositions, the `dynamicGeneration.basis` regeneration signal — but
 * the browser keeps serving the pre-edit React Query cache: suitability
 * verdicts in the custom portfolio builder, the dynamic composition on the
 * portfolio payload, analytics snapshots and completion.
 *
 * The invalidation is a blanket `["flowInstances"]` prefix on purpose: a
 * collection edit can affect any instance in the tenant and the client has no
 * way to know which. Inactive queries are only marked stale (refetched on next
 * mount), so on the settings route this costs nothing.
 *
 * `["publicFlow", …]` is deliberately not invalidated — those keys only ever
 * live in the client's own browser session, which never runs the settings UI.
 */
export function useInvalidateFlowCachesForCollectionEdit() {
  const qc = useQueryClient();
  return useCallback(() => {
    qc.invalidateQueries({ queryKey: ["flowInstances"] });
    // Ad-hoc key outside the namespace (custom-portfolio-builder.tsx).
    qc.invalidateQueries({ queryKey: ["existing-snapshot"] });
  }, [qc]);
}

/**
 * Invalidate every cached query that embeds a financial situation model after
 * the model is edited or deleted. The flow page query carries the flow's model
 * with its `settings` (the component settings dialog gates the Capacity
 * section on it — QL-364), and the flow instance query carries it for the
 * widget (its Capacity tab, expecting-changes question and field lists). Both
 * are blanket prefixes on purpose: a model can back any flow in the tenant and
 * the settings page has no way to know which.
 */
export function useInvalidateFlowCachesForFinancialModelEdit() {
  const qc = useQueryClient();
  return useCallback(() => {
    qc.invalidateQueries({ queryKey: ["flows"] });
    qc.invalidateQueries({ queryKey: ["flowInstances"] });
  }, [qc]);
}
